/**
 * Today's timeline — scheduled shifts with the work sessions that belong to them,
 * plus unscheduled work, in time order. Pure (pass `now`), so it's unit-testable.
 *
 * A session belongs to a shift when it overlaps the shift's scheduled window
 * widened by ATTACH_GRACE_MIN on each side — the same rule the backend's
 * ShiftService uses, so the timeline and the shift states agree.
 */

import { Shift, ShiftEvent, ShiftState } from '@/utils/shifts';

/** Must match ShiftService.ATTACH_GRACE_MINUTES. */
export const ATTACH_GRACE_MIN = 15;

export interface TimelineSession {
  id: number;
  inDate: Date;
  outDate: Date | null;
}

/** One scheduled event inside a shift (a shift is contiguous events, breaks ≤ 9 min). */
export interface TimelineEvent {
  key: string;
  label: string;
  start: Date;
  end: Date;
}

export type TimelineGroup<S extends TimelineSession> =
  | {
      kind: 'shift';
      key: string;
      label: string;
      start: Date;
      end: Date;
      state: ShiftState;
      /** The shift's events in time order (DENIED / ABSENT dropped). */
      events: TimelineEvent[];
      sessions: S[];
    }
  | {
      kind: 'unscheduled';
      key: string;
      sessions: S[];
    };

const CANCELS_OUT = new Set(['DENIED', 'ABSENT']);

const addMin = (d: Date, m: number) => new Date(d.getTime() + m * 60000);

const statusOf = (e: ShiftEvent) => (e.status ?? '').toUpperCase();

/** "HH:mm[:ss]" on the shift's day → Date; null if unparseable. */
const onDay = (day: Date, hhmm?: string): Date | null => {
  const m = hhmm ? /^(\d{1,2}):(\d{2})/.exec(hhmm) : null;
  if (!m) return null;
  const d = new Date(day);
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return d;
};

/** Same label rule as the backend's ShiftService.label (dept, then name, then type). */
const eventLabel = (e: ShiftEvent) => (e.dept || e.name || e.type || 'Shift').trim();

const eventsOf = (sh: Shift): TimelineEvent[] =>
  sh.events
    .filter((e) => !CANCELS_OUT.has(statusOf(e)))
    .map((e) => ({ e, start: onDay(sh.scheduledStart, e.start), end: onDay(sh.scheduledStart, e.end) }))
    .filter((x): x is { e: ShiftEvent; start: Date; end: Date } => !!x.start && !!x.end)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .map(({ e, start, end }) => ({
      key: `ev-${e.id}`,
      label: eventLabel(e),
      start,
      end,
    }));

/**
 * The event to name for "now" within a shift: the one in progress, else the
 * next one coming up, else the last one.
 */
export const currentEvent = (events: TimelineEvent[], now: Date): TimelineEvent | undefined =>
  events.find((e) => e.start <= now && now < e.end) ??
  events.find((e) => e.start > now) ??
  events[events.length - 1];

/** Shifts + sessions → chronological groups; consecutive unscheduled sessions share a group. */
export function buildTimeline<S extends TimelineSession>(
  shifts: Shift[],
  sessions: S[],
  now: Date
): TimelineGroup<S>[] {
  const scheduled = shifts
    .filter((s) => s.scheduled)
    // A shift made only of DENIED / ABSENT events isn't on the schedule (mirrors the backend filter).
    .filter((s) => s.events.length === 0 || s.events.some((e) => !CANCELS_OUT.has((e.status ?? '').toUpperCase())))
    .slice()
    .sort((a, b) => a.scheduledStart.getTime() - b.scheduledStart.getTime());

  const attached = new Map<string, S[]>();
  const loose: S[] = [];
  for (const s of [...sessions].sort((a, b) => a.inDate.getTime() - b.inDate.getTime())) {
    const end = s.outDate ?? now;
    const home = scheduled.find(
      (sh) =>
        s.inDate < addMin(sh.scheduledEnd, ATTACH_GRACE_MIN) &&
        end > addMin(sh.scheduledStart, -ATTACH_GRACE_MIN)
    );
    if (home) {
      attached.set(home.key, [...(attached.get(home.key) ?? []), s]);
    } else {
      loose.push(s);
    }
  }

  type Item = { at: number; group: TimelineGroup<S> };
  const items: Item[] = scheduled.map((sh) => ({
    at: sh.scheduledStart.getTime(),
    group: {
      kind: 'shift',
      key: sh.key,
      label: sh.label,
      start: sh.scheduledStart,
      end: sh.scheduledEnd,
      state: sh.state,
      events: eventsOf(sh),
      sessions: attached.get(sh.key) ?? [],
    },
  }));
  for (const s of loose) {
    items.push({ at: s.inDate.getTime(), group: { kind: 'unscheduled', key: `loose-${s.id}`, sessions: [s] } });
  }
  items.sort((a, b) => a.at - b.at);

  // Merge back-to-back unscheduled sessions into one group.
  const out: TimelineGroup<S>[] = [];
  for (const { group } of items) {
    const last = out[out.length - 1];
    if (group.kind === 'unscheduled' && last?.kind === 'unscheduled') {
      last.sessions.push(...group.sessions);
    } else {
      out.push(group);
    }
  }
  return out;
}

/**
 * The shift a clock-out was probably meant to clock in for: the next group is a
 * shift with no work under it, starting within ATTACH_GRACE_MIN of the clock-out
 * (clocked out 12:29, next class 12:30, no clock-in for it). Undefined otherwise.
 */
export function shiftAfterClockOut<S extends TimelineSession>(
  groups: TimelineGroup<S>[],
  groupIndex: number,
  session: S
): Extract<TimelineGroup<S>, { kind: 'shift' }> | undefined {
  const next = groups[groupIndex + 1];
  if (!session.outDate || next?.kind !== 'shift' || next.sessions.length > 0) return undefined;
  const gap = Math.abs(next.start.getTime() - session.outDate.getTime()) / 60000;
  return gap <= ATTACH_GRACE_MIN ? next : undefined;
}
